// Google Analytics utility functions
// Note: GA4 script is loaded in index.html

// Session timing storage
let sessionStartTime = null;
let pageStartTime = null;
let questionStartTime = null;

// Initialize session timing
export const initializeSession = () => {
  sessionStartTime = performance.now();
  pageStartTime = performance.now();
};

// Track page views with timing
export const trackPageView = (page) => {
  if (typeof window.gtag !== 'function') return;
  
  const currentTime = performance.now();
  let timeOnPreviousPage = null;
  
  if (pageStartTime !== null) {
    timeOnPreviousPage = Math.round(currentTime - pageStartTime);
  }
  
  window.gtag('event', 'page_view', {
    page_title: page,
    page_location: window.location.href,
    time_on_previous_page_ms: timeOnPreviousPage
  });
  
  pageStartTime = currentTime;
};

// Track landing page engagement - when user clicks begin
export const trackLandingPageClick = () => {
  if (typeof window.gtag !== 'function') return;
  
  const timeOnLanding = sessionStartTime ? Math.round(performance.now() - sessionStartTime) : null;
  
  window.gtag('event', 'landing_page_begin_click', {
    event_category: 'engagement',
    event_label: 'User clicked Begin from landing page',
    time_on_landing_ms: timeOnLanding
  });
};

// Track when user starts the question flow
export const trackBeginQuestions = () => {
  if (typeof window.gtag !== 'function') return;
  
  trackLandingPageClick();
  questionStartTime = performance.now();
  
  window.gtag('event', 'begin_questions', {
    event_category: 'engagement',
    event_label: 'User started question flow'
  });
};

// Track when a question is displayed
export const trackQuestionViewed = (questionId, categoryIndex, questionIndex, totalQuestions) => {
  if (typeof window.gtag !== 'function') return;
  
  questionStartTime = performance.now();
  const progressPercent = Math.round(((questionIndex + 1) / totalQuestions) * 100);
  
  window.gtag('event', 'question_viewed', {
    event_category: 'progress',
    event_label: `Q${categoryIndex + 1}.${questionIndex + 1}`,
    question_id: questionId,
    question_number: questionIndex + 1,
    total_questions: totalQuestions,
    progress_percent: progressPercent
  });
};

// Track question answers with timing
export const trackQuestionAnswer = (questionId, answer, categoryIndex, questionIndex, totalQuestions) => {
  if (typeof window.gtag !== 'function') return;
  
  const currentTime = performance.now();
  const timeOnQuestion = questionStartTime ? Math.round(currentTime - questionStartTime) : null;
  const progressPercent = Math.round(((questionIndex + 1) / totalQuestions) * 100);
  
  window.gtag('event', 'question_answered', {
    event_category: 'engagement',
    event_label: `Q${categoryIndex + 1}.${questionIndex + 1}`,
    question_id: questionId,
    question_number: questionIndex + 1,
    total_questions: totalQuestions,
    progress_percent: progressPercent,
    time_on_question_ms: timeOnQuestion,
    // NOTE: We do NOT track the actual answer for privacy
    has_answer: true
  });
  
  // Reset question timer for next question
  questionStartTime = currentTime;
};

// Track user abandonment at specific question
export const trackQuestionAbandonment = (questionId, categoryIndex, questionIndex, totalQuestions) => {
  if (typeof window.gtag !== 'function') return;
  
  const timeOnQuestion = questionStartTime ? Math.round(performance.now() - questionStartTime) : null;
  const progressPercent = Math.round(((questionIndex + 1) / totalQuestions) * 100);
  
  window.gtag('event', 'question_abandoned', {
    event_category: 'abandonment',
    event_label: `Abandoned at Q${categoryIndex + 1}.${questionIndex + 1}`,
    question_id: questionId,
    question_number: questionIndex + 1,
    total_questions: totalQuestions,
    progress_percent: progressPercent,
    time_before_abandon_ms: timeOnQuestion
  });
};

// Track question flow completion with comprehensive metrics
export const trackQuestionsComplete = (totalAnswers, totalQuestions) => {
  if (typeof window.gtag !== 'function') return;
  
  const sessionTime = sessionStartTime ? Math.round(performance.now() - sessionStartTime) : null;
  const completionRate = Math.round((totalAnswers / totalQuestions) * 100);
  
  window.gtag('event', 'questions_complete', {
    event_category: 'engagement',
    event_label: 'User completed all questions',
    value: totalAnswers,
    total_questions: totalQuestions,
    completion_rate: completionRate,
    total_session_time_ms: sessionTime
  });
};

// Track when user reaches the final summary page
export const trackSummaryPageReached = (totalAnswers, totalQuestions) => {
  if (typeof window.gtag !== 'function') return;
  
  const sessionTime = sessionStartTime ? Math.round(performance.now() - sessionStartTime) : null;
  
  window.gtag('event', 'summary_page_reached', {
    event_category: 'conversion',
    event_label: 'User reached final summary page',
    total_answers: totalAnswers,
    total_questions: totalQuestions,
    session_time_to_summary_ms: sessionTime
  });
};

// Track PDF downloads with conversion metrics
export const trackPDFDownload = (totalAnswers, totalQuestions) => {
  if (typeof window.gtag !== 'function') return;
  
  const sessionTime = sessionStartTime ? Math.round(performance.now() - sessionStartTime) : null;
  
  window.gtag('event', 'pdf_download', {
    event_category: 'conversion',
    event_label: 'User downloaded PDF summary',
    total_answers: totalAnswers,
    total_questions: totalQuestions,
    session_time_to_download_ms: sessionTime
  });
};

// Track when user goes back to previous questions
export const trackGoBack = (fromPage, currentQuestionNumber = null) => {
  if (typeof window.gtag !== 'function') return;
  
  window.gtag('event', 'navigation_back', {
    event_category: 'navigation',
    event_label: `Back from ${fromPage}`,
    current_question_number: currentQuestionNumber
  });
};

// Track when user starts over
export const trackStartOver = (fromPage, questionsAnswered = 0) => {
  if (typeof window.gtag !== 'function') return;
  
  const sessionTime = sessionStartTime ? Math.round(performance.now() - sessionStartTime) : null;
  
  window.gtag('event', 'start_over', {
    event_category: 'navigation',
    event_label: 'User started over',
    from_page: fromPage,
    questions_answered_before_restart: questionsAnswered,
    time_before_restart_ms: sessionTime
  });
  
  // Reset session timing
  sessionStartTime = performance.now();
};